/**
 * The dock's small wiring decisions, pure so `scripts/test-voice-dock-wiring.js`
 * can pin them without a browser.
 *
 * Fourteen things live here. Which spoken turn a request belongs to (`turnLink`
 * and `withTurnLink`); what a re-parsed transcript turns into (the same
 * three outcomes a relayed parse has: a proposal, a clarification, or nothing
 * usable); which state word a session shows once it is resting; what "live"
 * means for the footer now a dock can be open with no session at all; whether a
 * SPOKEN confirmation is still about the card on screen, by the clock and by
 * the card's own identity (`confirmationMatchesCard`); which ways a proposal
 * may be confirmed at all (`confirmModeOf`); whether a key event
 * came from a text field the user was typing into; whether an answer that has
 * come back over HTTP is still the newest one asked for; which voice a
 * sentence has to be spoken in; the two sentences that report the
 * microphone — the meter's caption and the gear's read-only line; what the
 * session has cost so far, across all three things that bill; and how much of
 * what it paid for was anybody talking.
 */

import { classifyToolCall } from "./voiceRelayEvents.js";

/**
 * The server's bound on a client-supplied turn reference, in characters.
 *
 * Quoted from `inventory-backend/controllers/commandController.js` and
 * `voiceParseController.js`, whose `turnRefFrom` trims and then slices to this
 * length. Truncating HERE too is what keeps the two halves agreeing: a turn
 * recorded under the first 64 characters of an id must be findable by the
 * preview that names it, and a client that sent 80 while the server stored 64
 * would be naming a turn that does not exist.
 */
export const TURN_REF_MAX_CHARS = 64;

/**
 * One turn reference, sanitised the way the server sanitises it.
 *
 * `""` is the relay saying it sent no turn id (`mapCommand` maps a missing
 * `turnId` to the empty string rather than to `"undefined"`), and a
 * whitespace-only id is the same thing with a space in it. Both are "not
 * known", which is null — never an empty string on the wire, because an empty
 * string is a value the server would store and then never match.
 *
 * @param {string|number|null|undefined} value
 * @returns {string|null}
 */
function turnRef(value) {
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  if (typeof value !== "string") return null;
  const text = value.trim();
  return text ? text.slice(0, TURN_REF_MAX_CHARS) : null;
}

/**
 * Which spoken turn a card belongs to, from the proposal that raised it.
 *
 * The transcript log (`voiceturns`) links a preview and a receipt back to the
 * utterance by matching ARGUMENTS and time, because the dock never told it
 * which turn a request was for. That match is deliberately strict and misses
 * whenever the dock's resolved identifiers differ from the parser's raw ones —
 * which is precisely what a preview does to them — so the admin funnel showed
 * applies running ahead of previews. These two identifiers make the link exact.
 *
 * Both are OPTIONAL server-side and neither names a lab or a user: the scope
 * comes from the authenticated request, as it always did. A wrong id therefore
 * costs an unlinked row, never a wrong write.
 *
 * Captured with the proposal and never re-read at confirm time. "Resume voice"
 * mints a SECOND relay session with a second id (`resetForNewSession` nulls the
 * session and starts a new one while deliberately keeping the card), so a link
 * read at the tap would name the session that happened to be alive then rather
 * than the one that produced the words on screen.
 *
 * @param {{callId?: string|number, voiceSessionId?: string}} [source]
 *   the proposal's call id — the relay's `turnId` as a string, `""` when it
 *   sent none — and the session id the relay reported on `voice:ready`.
 * @returns {{turnId: string|null, voiceSessionId: string|null}}
 */
export function turnLink({ callId, voiceSessionId } = {}) {
  return {
    turnId: turnRef(callId),
    voiceSessionId: turnRef(voiceSessionId),
  };
}

/**
 * A request body with the turn link's KNOWN keys added, and only those.
 *
 * A key is omitted when it is not known rather than sent empty or null. The
 * three endpoints that take these (`/commands/:intent/preview`, `/apply` and
 * `/voice/parse`) treat every one as optional and fall back to matching on
 * arguments when a key is absent; an empty string, by contrast, is a value they
 * would carry into the query and fail to match on.
 *
 * `editedFrom` rides in the same object because it is the same kind of fact —
 * the turn this one corrects — and only `/voice/parse` is ever given it.
 *
 * The body is copied, never mutated: `pendingFields` is the card's own state
 * and a retry re-sends it.
 *
 * @param {object} body the request body so far
 * @param {{turnId?: string|null, voiceSessionId?: string|null,
 *   editedFrom?: string|null}|null} [link]
 * @returns {object} a new body
 */
export function withTurnLink(body, link) {
  const base = body && typeof body === "object" ? body : {};
  const voiceSessionId = turnRef(link && link.voiceSessionId);
  const turnId = turnRef(link && link.turnId);
  const editedFrom = turnRef(link && link.editedFrom);
  return {
    ...base,
    ...(voiceSessionId ? { voiceSessionId } : {}),
    ...(turnId ? { turnId } : {}),
    ...(editedFrom ? { editedFrom } : {}),
  };
}

/**
 * A `POST /api/v1/voice/parse` result, as the store's `parseVoiceTranscript`
 * resolves it, mapped onto the proposal path's vocabulary.
 *
 * `classifyToolCall` is the relay mapper's own classifier, so a re-parse and a
 * relayed parse cannot disagree about what `preview_move_box` or
 * `request_inventory_clarification` mean.
 *
 * The caller may name the call id. The re-parse endpoint RECORDS the edit under
 * a turn id the client supplies (`voiceParseController.js` falls back to a
 * random `http-` one only when none is sent) and returns only the parse itself,
 * so the dock mints the id before the request and hands the same string to the
 * proposal. A later preview then names the EDIT turn rather than the original.
 *
 * @param {{tool?: string, args?: object, latencyMs?: number}|null} result
 * @param {{callId?: string}} [options] the turn id the edit was sent under
 * @returns {{kind:'proposal', callId:string, intent:string, fields:object, latencyMs:number|null}
 *   | {kind:'clarification', fields:object}
 *   | {kind:'unusable'}}
 */
export function reparseOutcome(result, { callId } = {}) {
  if (!result || typeof result !== "object") return { kind: "unusable" };
  const call = classifyToolCall(
    result.tool || null,
    JSON.stringify(result.args || {})
  );
  if (call.kind === "clarification") {
    return { kind: "clarification", fields: call.fields };
  }
  if (call.kind === "proposal") {
    return {
      kind: "proposal",
      callId: turnRef(callId) || "edit-" + Date.now(),
      intent: call.intent,
      fields: call.fields,
      latencyMs: Number.isFinite(result.latencyMs) ? result.latencyMs : null,
    };
  }
  return { kind: "unusable" };
}

/**
 * The words a session may still be moved off: the two it rests on, and the
 * three it counts through while it is starting up.
 *
 * The startup words belong in here, and leaving them out was the whole of the
 * dock's stuck "Connecting". The mapper emits `listening` when the relay says
 * `voice:ready`, and at that instant the phase is `connecting` — so a function
 * that returned any phase but `listening`/`ready` unchanged handed `connecting`
 * straight back and nothing ever moved it again for the life of the session.
 *
 * They are only moved BY that emit. The gate sites (a hold pressed, a hold
 * released, the gear) guard themselves down to the two resting words, because
 * the startup words are the session's own progress report and a hold that
 * relabelled "Requesting microphone" as "Listening" would be claiming a
 * microphone the browser has not granted yet.
 */
const MOVEABLE_PHASES = ["listening", "ready", "starting", "requesting", "connecting"];

/**
 * The state word for a session that is resting — not mid-command.
 *
 * Three facts decide it, in this order.
 *
 * A phase that is not one of `MOVEABLE_PHASES` is left exactly as it is, so a
 * proposal, a question or a stopped session keeps its own word.
 *
 * `live === false` is "Ready" whatever the gate says. A dock with no session,
 * or one whose session has ended, has nothing capturing audio, and the word
 * "Listening" over a microphone that does not exist is the one thing this
 * function must never say — it is what the gear said when hands-free was
 * chosen with no session behind it.
 *
 * `streaming === false` is the same rule for the second of the two lifetimes.
 * A session whose relay stream was torn down for being idle is still a session:
 * its microphone is open, its card is on screen and its Confirm button works.
 * But nothing is reaching the relay, so "Listening" would be describing a wire
 * that is not there. It rests on "Ready" until the next tap re-opens it. The
 * default is `true` so every caller that predates the teardown is unchanged.
 *
 * Then the gate: hold-to-talk with the button up is "Ready", because the
 * microphone is captured but every frame leaves as silence. Everything else —
 * hands-free, or a hold in progress — is "Listening".
 *
 * @param {{holdMode?: boolean, holding?: boolean, phase?: string,
 *   live?: boolean, streaming?: boolean}} options
 * @returns {string} a key of the dock's `STATE_WORDS`
 */
export function listeningPhase({
  holdMode,
  holding,
  phase,
  live,
  streaming,
} = {}) {
  if (phase && MOVEABLE_PHASES.indexOf(phase) === -1) return phase;
  if (live === false) return "ready";
  if (streaming === false) return "ready";
  if (holdMode && !holding) return "ready";
  return "listening";
}

/**
 * Whether a confirmation may still be acted on, given when the card under it
 * was last REPLACED.
 *
 * A tap is guarded already, and by a different clock: `REPLACEMENT_TAP_GUARD_MS`
 * disables the Confirm button for a second so a finger committed to the old
 * card cannot land on the new one. A spoken "confirm" has a longer flight —
 * said over the old card, recognised, and delivered as a final transcript
 * 0.2–1.5 s later — and it arrived through a gate (`confirmIntent` and an open
 * window) that the REPLACEMENT itself re-satisfies. So the spoken word applied
 * whatever card happened to be on screen when it landed, with that card's hash
 * and its idempotency key: a move or a create the user never read.
 *
 * Only a replacement arms this. `replacedAt` is 0 for a first card, and
 * `now - 0` is never under the guard, so the ordinary case is unguarded.
 *
 * @param {{source?: string, replacedAt?: number, now?: number, guardMs?: number}} options
 * @returns {boolean} false only for a spoken confirm inside a replacement's window
 */
export function voiceConfirmAllowed({ source, replacedAt, now, guardMs } = {}) {
  if (source !== "voice") return true;
  if (!Number.isFinite(replacedAt) || replacedAt <= 0) return true;
  if (!Number.isFinite(now) || !Number.isFinite(guardMs)) return true;
  return now - replacedAt >= guardMs;
}

/**
 * Whether a confirmation is about the card that is on the screen.
 *
 * The relay opens a confirm window for ONE proposal and records the epoch it
 * opened for: the turn, and the hash of the preview that was read back. It
 * sends that hash with the decision. The dock holds the same hash on the card
 * — it is the value `expectedPreviewHash` binds the apply to — so the two are
 * comparable, and a decision minted against a card that is no longer on screen
 * is refused before it can reach the one function that writes.
 *
 * This is the second half of a guard whose first half is `voiceConfirmAllowed`,
 * and they answer different questions. That one is arithmetic on a clock: was a
 * replacement rendered within the last `VOICE_REPLACEMENT_GUARD_MS`. This one
 * is identity: is this the exact card the word was said over. A stale final that
 * arrives after the guard has expired — a slow recogniser, a relay backlog, a
 * coworker who said "confirm" to their own screen near this microphone — passes
 * the clock and fails here.
 *
 * A confirmation carrying NO hash is accepted. That is the deployment, not a
 * hole: the relay is a separate service and today's one records no epoch at
 * all, so refusing an unhashed confirmation would take the spoken word away
 * from every session until both halves have shipped. A relay that sends a hash
 * is held to it from its first message.
 *
 * @param {{eventHash?: string|number|null, cardHash?: string|number|null}} [options]
 * @returns {boolean}
 */
export function confirmationMatchesCard({ eventHash, cardHash } = {}) {
  const spoken = hashRef(eventHash);
  if (!spoken) return true;
  return spoken === hashRef(cardHash);
}

/** A preview hash as a comparable value: a non-empty string, or null. */
function hashRef(value) {
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  if (typeof value !== "string") return null;
  const text = value.trim();
  return text || null;
}

/**
 * Which way a proposal may be confirmed, from what the relay said on it.
 *
 * `tap` is the shared-terminal rule (`Lab.voiceSharedTerminal`): in a room
 * where one microphone sits within earshot of several people, a spoken word is
 * not evidence that the person who read the card is the person who said it, so
 * the relay opens no spoken window for the command and says so here.
 *
 * Everything else is `voice` — including the absent field every relay sends
 * today, and including a value this build does not recognise. Both directions
 * of that default are deliberate: an unknown word must not quietly disable the
 * spoken window a lab is relying on, and must not quietly enable one in a lab
 * that turned it off, so only the exact word `tap` means tap.
 *
 * Deliberately a word rather than a boolean, because the wire carries a word
 * and a `tapOnly` flag here would be a value derived from one nobody checked.
 *
 * @param {string|null|undefined} value the relay's `confirmMode`
 * @returns {'tap'|'voice'}
 */
export function confirmModeOf(value) {
  return value === "tap" ? "tap" : "voice";
}

/**
 * Whether an event's target is somewhere the user is typing.
 *
 * The dock binds Escape on the DOCUMENT, deliberately: the page behind it stays
 * interactive, and the dock does not take focus away from whatever the user was
 * doing. The cost of that is that Escape pressed to clear a half-typed serial
 * out of the page's own search box — the input the microphone button sits
 * beside — reached the dock's handler and cancelled the card being read, or
 * closed the dock, with no message at all.
 *
 * The same predicate `VoiceHoldButton` uses for the space bar, copied rather
 * than imported: a util importing a component would invert the dependency, and
 * this file must stay loadable by a node test with no Vue at all.
 *
 * @param {{nodeType?: number, tagName?: string, isContentEditable?: boolean}|null} target
 * @returns {boolean}
 */
export function isTypingTarget(target) {
  if (!target || target.nodeType !== 1) return false;
  if (target.isContentEditable) return true;
  const tag = String(target.tagName || "").toUpperCase();
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
}

/**
 * Whether the footer should treat the session as able to hear.
 *
 * Before the first hold there is no session object at all; after a timer runs
 * out there is one that has ended. Neither can hear, and neither should offer
 * Confirm or "Stop voice".
 */
export function isSessionLive({ session, sessionEnded } = {}) {
  return !!session && !sessionEnded;
}

/**
 * Whether an HTTP answer still belongs to the newest thing the user asked for.
 *
 * The dock keeps one counter, `proposalGeneration`. Everything that INVALIDATES
 * what is on screen bumps it — a superseding proposal, a close, a new session,
 * and (as of this fix) a transcript edit — and every awaited round trip
 * captures the counter before it starts and compares it after. A mismatch means
 * the screen moved on while the request was in flight, and the answer must be
 * dropped rather than rendered.
 *
 * THE RULE, stated once because it is the whole of the guard: a generation is
 * bumped by every path that clears the card, and NOT bumping it is what let a
 * superseded preview render. `onReparse` used to clear the card and leave the
 * counter alone, so the ORIGINAL preview's response — a well the user had
 * already corrected away from — passed the newest-generation check while the
 * re-parse was still running, rendered a live card, and armed Confirm on it.
 *
 * Deliberately strict equality on the raw values: two NaNs compare unequal and
 * so read as stale, which discards the answer. Discarding a good answer costs a
 * repeat; rendering a stale one applies a command nobody read.
 *
 * @param {number} started the counter's value when the request went out
 * @param {number} current the counter's value now
 * @returns {boolean} true when the answer must be dropped
 */
export function isStaleGeneration(started, current) {
  return started !== current;
}

/**
 * The relay's hard ceiling on one read-back, in characters.
 *
 * The server's own constant (`inventory-backend/services/inworldTts.js`
 * `MAX_TEXT_CHARS`), quoted here rather than approximated. `normalizeSpeechText`
 * trims and then REFUSES anything longer, answering `unavailable('invalid')`.
 */
export const RELAY_SAY_LIMIT = 220;

/**
 * Which voice says a sentence.
 *
 * The dock used to send `sentence.slice(0, 220)` to the relay. A long box title
 * makes a proposal longer than that — "Move <210 characters> from VP1 well 1 to
 * VP14 well 7. Say confirm to apply." is 278 — so what the user HEARD ended
 * mid-clause at "from", with neither the source nor the destination spoken. The
 * relay's `done` for that truncated text is a normal end, not a `partial`, so
 * the dock opened the 12 s confirm window over a read-back that had never named
 * either location.
 *
 * A sentence that does not fit therefore does not go to the relay at all: the
 * browser's `speechSynthesis` has no length limit and says the whole thing, and
 * the window opens on ITS end. This is the same road `unavailable` and a
 * truncated (`partial`) relay turn already take.
 *
 * The boundary is inclusive because the server's is: exactly `relayLimit`
 * characters is accepted upstream, one more is refused.
 *
 * @param {string} sentence the trimmed sentence about to be spoken
 * @param {{relayLimit?: number}} [options]
 * @returns {'relay'|'browser'}
 */
export function speechRoute(sentence, { relayLimit = RELAY_SAY_LIMIT } = {}) {
  const text = String(sentence == null ? "" : sentence);
  const limit = Number.isFinite(relayLimit) ? relayLimit : RELAY_SAY_LIMIT;
  return text.length > limit ? "browser" : "relay";
}

/**
 * The meter's caption: what the microphone is doing, in the user's words.
 *
 * It describes the DEVICE and never the session, which is why it is the one
 * line that can say "open" on a panel with no session behind it at all — the
 * dock pre-opens the microphone when it appears, and a caption reading
 * "closed" over a live capture would be the one lie the recording indicator
 * would immediately contradict.
 *
 * `opening` is the pre-open in flight. It carries the same sentence the two
 * startup phases do, because to the person waiting it IS the same wait — and
 * the pre-open happens in the `ready` phase, where neither of those words is
 * ever set.
 *
 * The label is appended rather than replacing the sentence: "Microphone open"
 * is the fact, and which microphone is the detail that made ten seconds of
 * silence explicable.
 *
 * @param {{open?: boolean, opening?: boolean, phase?: string, paused?: boolean,
 *   preview?: boolean, label?: string}} state
 * @returns {string}
 */
export function micMeterCaption({
  open,
  opening,
  phase,
  paused,
  preview,
  label,
} = {}) {
  if (!open) {
    return opening || phase === "starting" || phase === "requesting"
      ? "Opening the microphone"
      : "Microphone closed";
  }
  // True, and worth saying: capture starts before the socket does and the
  // frames are held, so words spoken during the handshake are not lost.
  if (phase === "connecting") return "Microphone open, holding audio";
  if (paused && preview) return "Microphone paused while you check this";
  if (paused) return "Microphone paused";
  return label ? `Microphone open · ${label}` : "Microphone open";
}

/**
 * The gear's read-only microphone line.
 *
 * Two facts, and only when they exist: which device the browser handed over,
 * and how long the handover took. The second is there because a ten-second
 * handover looks exactly like a broken application until it is a number, and
 * because the number is what tells the user the NEXT press will be instant —
 * that wait has already been paid.
 *
 * An empty string means "say nothing", which is the honest answer before a
 * microphone has ever been opened. A device with no label — a browser that
 * withholds it until permission is remembered — also says nothing rather than
 * printing an empty name.
 *
 * @param {{label?: string, openMs?: number}|null} info the session's `mic-info`
 * @returns {string} the line, or "" when there is nothing to report
 */
export function micSettingsLine(info) {
  const label = info && typeof info.label === "string" ? info.label.trim() : "";
  if (!label) return "";
  const openMs = info && Number.isFinite(info.openMs) ? info.openMs : null;
  if (openMs === null || openMs < 0) return `Microphone: ${label}`;
  return `Microphone: ${label} (opened in ${(openMs / 1000).toFixed(1)} s)`;
}

/**
 * Muse streaming STT, per audio-minute. The rate this pipeline actually bills
 * at, replacing the xAI realtime figure of $0.08 the takeover quoted.
 */
export const AUDIO_USD_PER_MINUTE = 0.003;

/**
 * Inworld TTS, per million characters — the relay's read-back voice. Counted
 * on the `chars` the relay itself reports in its `voice:speak` terminator, not
 * on the length of the sentence the dock sent, because those differ whenever a
 * turn is cut short.
 */
export const TTS_USD_PER_MILLION_CHARS = 15;

/** A non-negative finite number, or zero. Anything else is "did not happen". */
function amount(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/** Round UP to the mil (a tenth of a cent). See `costBreakdown`. */
function mils(usd) {
  return Math.ceil(usd * 1000) / 1000;
}

/**
 * What this session has cost, split three ways.
 *
 * The footer used to show only the first of these, which made the other two
 * free on screen: a session that spent ten seconds listening and then parsed
 * four commands and read four proposals back cost several times what the dock
 * admitted to.
 *
 * Rounding is UP, per component, at the mil — the rule the audio-only number
 * already used, kept because a display that can understate what a session cost
 * is the one kind of wrong that matters here. Rounding each part rather than
 * the sum is what makes the tooltip's three numbers add up to the footer's one;
 * the cost of that is at most two tenths of a cent of overstatement, which is
 * the safe direction.
 *
 * @param {{seconds?: number, parseUsd?: number, ttsChars?: number}} totals
 *   the session's running totals: audio seconds streamed, dollars the relay
 *   reported for its parses, characters the relay reported having synthesised.
 * @returns {{voice: number, parse: number, readback: number, total: number}}
 */
export function costBreakdown({ seconds, parseUsd, ttsChars } = {}) {
  const voice = mils((amount(seconds) * AUDIO_USD_PER_MINUTE) / 60);
  const parse = mils(amount(parseUsd));
  const readback = mils(
    (amount(ttsChars) * TTS_USD_PER_MILLION_CHARS) / 1000000
  );
  return {
    voice,
    parse,
    readback,
    // Summed at the mil, then snapped back onto it: three values that are each
    // an exact number of mils still add up to 0.005000000000000001 in binary
    // floating point, and that is what `toFixed(3)` would be handed.
    total: Math.round((voice + parse + readback) * 1000) / 1000,
  };
}

/**
 * The one number the footer prints. Zero when nothing has happened yet, which
 * is the honest answer for a dock that is open and has never listened.
 *
 * @param {{seconds?: number, parseUsd?: number, ttsChars?: number}} totals
 * @returns {number} dollars, already rounded to the mil
 */
export function estimateCost(totals) {
  return costBreakdown(totals).total;
}

/**
 * How many seconds were paid for per second anyone spoke.
 *
 * Muse bills by the audio minute for as long as the stream is open, silence
 * included, so the gap between these two numbers IS the waste — a panel left
 * open through a forty-second pause with six seconds of speech in it reads 6.7.
 * One number, so the admin page can sort by it and a lab can be shown the
 * before and after of this packet rather than told about it.
 *
 * `null` rather than a number when nobody spoke: a ratio over zero is not a
 * large number, it is not a ratio, and Infinity on a screen reads as a bug.
 *
 * @param {{billedSeconds?: number, spokenSeconds?: number}} [totals]
 * @returns {number|null} billed ÷ spoken, to two decimals
 */
export function billedVsSpoken({ billedSeconds, spokenSeconds } = {}) {
  const billed = amount(billedSeconds);
  const spoken = amount(spokenSeconds);
  if (spoken <= 0) return null;
  return Math.round((billed / spoken) * 100) / 100;
}
